import { computed, ref, type ComputedRef } from 'vue';
import type { Capability } from './capabilities';
import type { Profile } from './profiles';
import type { Scopes, ScopeKey } from './scopes';
import { AuthSession, decodeJwtPayload } from './session';

export interface RouteRecordWithRequires {
  meta?: { requires?: Capability };
}

function readStoredToken(): string | null {
  return localStorage.getItem('access_token');
}

const rawToken = ref<string | null>(readStoredToken());

/** Vuelve a leer el token: login, refresh, logout e impersonación. */
export function refreshAuthSession(): void {
  rawToken.value = readStoredToken();
}

const payload = computed(() =>
  rawToken.value ? decodeJwtPayload(rawToken.value) : null,
);

const session = computed(() => AuthSession.fromToken(payload.value));

export const authSession: ComputedRef<AuthSession> = session;

export function useAuth() {
  const can = (capability: Capability): ComputedRef<boolean> =>
    computed(() => session.value.canAccess(capability));

  const scope = <K extends ScopeKey>(key: K): ComputedRef<Scopes[K]> =>
    computed(() => session.value.scope(key));

  const profiles: ComputedRef<readonly Profile[]> = computed(
    () => session.value.profiles,
  );

  const isReady: ComputedRef<boolean> = computed(() => payload.value !== null);

  const isStaff: ComputedRef<boolean> = computed(() => session.value.isStaff);

  const sessionEmail: ComputedRef<string | null> = computed(() => {
    const current = payload.value;
    if (!current) return null;
    if (current.exp && current.exp * 1000 <= Date.now()) return null;
    return current.email ?? current.preferred_username ?? null;
  });

  const canAccessRoute = (
    matched: readonly RouteRecordWithRequires[],
  ): boolean =>
    matched.every(record => session.value.canAccess(record.meta?.requires));

  return {
    can,
    scope,
    profiles,
    isReady,
    isStaff,
    sessionEmail,
    canAccessRoute,
  };
}
